import { addDays } from '@/domain/reservations/reservation-time';

/** A stats period in branch dates (yyyy-MM-dd): business days, or calendar days. */
export interface IStatsPeriod {
  first: string;
  /** Last day included. */
  last: string;
  /** An end has hour precision: a window of hours, not whole days. */
  partial: boolean;
}

/** The stats API range a tool sends (`normalizeStatsRange`): yyyyMMdd or yyyyMMddHH. */
export interface IStatsApiRange {
  from: string;
  to: string | null;
  interval: string | null;
}

const API_DATE = /^(\d{4})(\d{2})(\d{2})(\d{2})?$/;

function readApiDate(value: string): { date: string; hasHour: boolean } {
  const match = API_DATE.exec(value);
  if (!match) throw new RangeError(`Invalid stats date: "${value}"`);
  return {
    date: `${match[1]}-${match[2]}-${match[3]}`,
    hasHour: match[4] !== undefined,
  };
}

/** `2026-10-05` → `20261005`: a whole day for the stats API. */
export function toApiDate(date: string): string {
  return date.replace(/-/g, '');
}

/**
 * The days a stats API range covers. A date `to` is the last day included,
 * except in hourly mode, where a whole-day range ends at the next day's start.
 */
export function statsPeriod(range: IStatsApiRange): IStatsPeriod {
  const from = readApiDate(range.from);
  if (range.to === null) {
    return { first: from.date, last: from.date, partial: from.hasHour };
  }
  const to = readApiDate(range.to);
  const last =
    !to.hasHour && range.interval === 'hourly' ? addDays(to.date, -1) : to.date;
  return {
    first: from.date,
    last,
    partial: from.hasHour || to.hasHour,
  };
}

/** Days in a whole-day period. */
export function periodDays(period: IStatsPeriod): number {
  const ms =
    Date.parse(`${period.last}T00:00:00Z`) -
    Date.parse(`${period.first}T00:00:00Z`);
  return Math.round(ms / 86_400_000) + 1;
}

/**
 * The period a stats card compares with (plan §9.7): one day compares with
 * the same weekday 7 days earlier, n whole days with the n days before. Hour
 * windows and periods that reach the current (unfinished) day compare with
 * nothing: their numbers are still growing.
 */
export function comparisonRange(
  period: IStatsPeriod,
  currentDate: string
): IStatsPeriod | null {
  if (period.partial || period.last >= currentDate) return null;
  const days = periodDays(period);
  if (days === 1) {
    const sameWeekday = addDays(period.first, -7);
    return { first: sameWeekday, last: sameWeekday, partial: false };
  }
  return {
    first: addDays(period.first, -days),
    last: addDays(period.first, -1),
    partial: false,
  };
}

/** A whole-day period as stats API params (no `to` for one day). */
export function statsPeriodParams(
  period: IStatsPeriod
): {
  from: string;
  to: string | null;
} {
  return {
    from: toApiDate(period.first),
    to: period.first === period.last ? null : toApiDate(period.last),
  };
}
